import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, DropResult, Droppable } from "@hello-pangea/dnd";
import { Stage } from "views/settings-crm/types/stage";
import { LeadsFilters } from "shared/app/LeadsTableView/types";
import { LeadType } from "views/leads-id/types";
import { useChangeLeadStage } from "shared/app/LeadsTableView/hooks/useChangeLeadStage";
import {
  moveStageInOrder,
  sortStagesByOrder
} from "shared/app/LeadsTableView/utils/stageOrder";
import { PipelineColumn } from "./PipelineColumn";
import {
  leadIdFromCardDraggableId,
  PIPELINE_COLUMN_DND_TYPE,
  PIPELINE_COLUMNS_DROPPABLE_ID
} from "./constants";

const EMPTY_STAGES: Stage[] = [];

interface LeadsPipelineViewProps {
  stagesData: Stage[];
  // Owner-pipeline stages; empty for partners without the owner-leads feature.
  ownerStagesData?: Stage[];
  filters?: LeadsFilters;
  search?: string;
  assigneeIds: number[] | null;
  starred: boolean;
  enabled: boolean;
  isLeadScoringEnabled: boolean;
  stageOrder?: string[] | null;
  onStageOrderChange?: (nextOrder: string[]) => void;
}

export const LeadsPipelineView = ({
  stagesData,
  ownerStagesData = EMPTY_STAGES,
  filters,
  search,
  assigneeIds,
  starred,
  enabled,
  isLeadScoringEnabled,
  stageOrder,
  onStageOrderChange
}: LeadsPipelineViewProps) => {
  const { mutate: changeStage } = useChangeLeadStage();

  const boardRef = useRef<HTMLDivElement | null>(null);
  const [boardTop, setBoardTop] = useState(0);
  // dnd can't take cards appended mid-drag, so columns pause auto-loading.
  const [isDragging, setIsDragging] = useState(false);

  // The FilterBadge row above the board mounts/unmounts with active filters,
  // which shifts the board's top — so re-measure when this toggles too.
  const hasActiveFilters = Boolean(
    filters?.stages?.length ||
    filters?.properties?.length ||
    filters?.leadSources?.length ||
    filters?.activityTypes?.length ||
    filters?.leadTypes?.length ||
    filters?.approvalLikelihoodBands?.length ||
    filters?.createdWithin ||
    filters?.createdAfter ||
    filters?.createdBefore ||
    filters?.showingWithin ||
    filters?.showingAfter ||
    filters?.showingBefore
  );

  // Pin the board to the viewport so the board itself (not the page, and not
  // each column) is the vertical scroll container — that's what lets
  // @hello-pangea/dnd auto-scroll while dragging a card up/down, mirroring how
  // the board is the horizontal scroller below. Columns keep their natural
  // height; only the board scrolls. Re-measure on resize and when the filter
  // row toggles.
  useLayoutEffect(() => {
    const measure = () => {
      const board = boardRef.current;
      if (board) setBoardTop(board.getBoundingClientRect().top);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [hasActiveFilters]);

  // Make the board itself the horizontal scroll container. Its flex ancestors
  // default to min-width:auto, so the columns' width propagates up and a far
  // ancestor becomes the scroller instead of the board — which breaks
  // @hello-pangea/dnd's auto-scroll and drop-position tracking (it follows the
  // board, not the element that actually scrolls). Letting those ancestors
  // shrink makes the board clip+scroll, so the library scrolls the right element
  // and the placeholder lands in the hovered column. Scoped to this view and
  // reverted on unmount so other pages are untouched.
  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return;

    const touched: HTMLElement[] = [];
    let el = board.parentElement;
    while (el && el !== document.body) {
      const { overflowX } = getComputedStyle(el);
      const isScroller =
        (overflowX === "auto" || overflowX === "scroll") &&
        el.scrollWidth > el.clientWidth;
      if (isScroller) break; // reached the outer scroll container — leave it
      el.style.minWidth = "0";
      touched.push(el);
      el = el.parentElement;
    }

    return () => {
      touched.forEach((node) => {
        node.style.minWidth = "";
      });
    };
  }, []);

  // Every stage across both pipelines, de-duped by id in case the two groups
  // ever share a stage record.
  const allStages = useMemo(() => {
    const seen = new Set<string>();
    return [...stagesData, ...ownerStagesData].filter((stage) => {
      const key = String(stage.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [stagesData, ownerStagesData]);

  // The board shows both lead-type pipelines by default; filtering by a single
  // Type narrows the columns to that pipeline's stages.
  const leadTypes = filters?.leadTypes;
  const typeScopedStages = useMemo(() => {
    const onlyOwner =
      leadTypes?.length === 1 && leadTypes[0] === LeadType.Owner;
    const onlyTenant =
      leadTypes?.length === 1 && leadTypes[0] === LeadType.Tenant;
    if (onlyOwner) return ownerStagesData;
    if (onlyTenant) return stagesData;
    return allStages;
  }, [stagesData, ownerStagesData, allStages, leadTypes]);

  const orderedStages = useMemo(
    () => sortStagesByOrder(typeScopedStages, stageOrder),
    [typeScopedStages, stageOrder]
  );

  // Show every scoped stage by default; when stage filters are applied, only
  // those stages become columns.
  const visibleStages = useMemo(() => {
    const selectedStages = filters?.stages;
    if (!selectedStages?.length) return orderedStages;
    const selectedIds = new Set(selectedStages.map((selected) => selected.id));
    return orderedStages.filter((stage) => selectedIds.has(String(stage.id)));
  }, [orderedStages, filters?.stages]);

  const onDragStart = useCallback(() => setIsDragging(true), []);

  const reorderColumns = useCallback(
    ({ source, destination }: DropResult) => {
      if (!destination || destination.index === source.index) return;

      onStageOrderChange?.(
        moveStageInOrder({
          allOrderedIds: sortStagesByOrder(allStages, stageOrder).map((stage) =>
            String(stage.id)
          ),
          visibleOrderedIds: visibleStages.map((stage) => String(stage.id)),
          from: source.index,
          to: destination.index
        })
      );
    },
    [allStages, stageOrder, visibleStages, onStageOrderChange]
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      setIsDragging(false);

      if (result.type === PIPELINE_COLUMN_DND_TYPE) {
        reorderColumns(result);
        return;
      }

      const { source, destination, draggableId } = result;
      if (!destination || source.droppableId === destination.droppableId)
        return;

      // Owner and tenant leads run on separate pipelines, so a card can only
      // move between stages of its own pipeline — block cross-pipeline drops.
      const isOwnerStage = (stageId: string) =>
        ownerStagesData.some((stage) => String(stage.id) === stageId);
      if (
        isOwnerStage(source.droppableId) !==
        isOwnerStage(destination.droppableId)
      )
        return;

      const destStage = [...stagesData, ...ownerStagesData].find(
        (stage) => String(stage.id) === destination.droppableId
      );
      if (!destStage) return;

      const leadId = leadIdFromCardDraggableId(draggableId);
      if (Number.isNaN(leadId)) return;

      changeStage({ leadId, stage: destStage });
    },
    [stagesData, ownerStagesData, changeStage, reorderColumns]
  );

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <Droppable
        droppableId={PIPELINE_COLUMNS_DROPPABLE_ID}
        direction="horizontal"
        type={PIPELINE_COLUMN_DND_TYPE}
      >
        {(provided) => (
          <div
            ref={(node) => {
              provided.innerRef(node);
              boardRef.current = node;
            }}
            {...provided.droppableProps}
            className="flex min-w-0 items-start gap-4 overflow-auto pb-4"
            style={
              boardTop
                ? { height: `calc(100dvh - ${boardTop}px - 16px)` }
                : undefined
            }
          >
            {visibleStages.map((stage, index) => (
              <PipelineColumn
                key={stage.id}
                stage={stage}
                index={index}
                filters={filters}
                search={search}
                assigneeIds={assigneeIds}
                starred={starred}
                enabled={enabled}
                isLeadScoringEnabled={isLeadScoringEnabled}
                isDragging={isDragging}
                scrollRootRef={boardRef}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
